import { ReactElement, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { I18n } from 'common'
import {
  SortOrder,
  SortOrderItem,
} from '../../../../../../src/store/models/SearchModels'
import { ReactSelect } from '../../../../../../src/ui-components/forms/ReactSelect'
import { defaultTheme } from '../../../../../../src/theme'
import { DEFAULT_SORT_ORDER } from '../../../constants'
import { getSortOptions } from '../../../utils/GetSortOptions'

interface Option {
  label: string
  value: string
}

interface FilterSortDrawerSortFieldsProps {
  sortOrder: SortOrder
  onChange: (sortOrder: SortOrder) => void
}

const matchSortOption = (
  item: SortOrderItem | undefined,
  options: SortOrderItem[]
): SortOrderItem | undefined => {
  if (!item) {
    return undefined
  }
  return (
    options.find(
      (option) => option.property === item.property && option.dir === item.dir
    ) ?? item
  )
}

const resolveSortSelection = (
  sortOrder: SortOrder,
  options: SortOrderItem[]
): { sortBy: SortOrderItem; thenBy: SortOrderItem } => {
  const defaults = DEFAULT_SORT_ORDER
  const sortBy =
    matchSortOption(sortOrder[0], options) ??
    matchSortOption(defaults[0], options) ??
    options[0]
  const thenBy =
    matchSortOption(sortOrder[1], options) ??
    matchSortOption(defaults[1], options) ??
    options[1] ??
    options[0]

  return { sortBy, thenBy }
}

export const FilterSortDrawerSortFields = ({
  sortOrder,
  onChange,
}: FilterSortDrawerSortFieldsProps): ReactElement => {
  const options = useMemo(() => getSortOptions(), [])
  const resolved = useMemo(
    () => resolveSortSelection(sortOrder, options),
    [options, sortOrder]
  )

  const [selectedSortByOption, setSelectedSortByOption] =
    useState<SortOrderItem>(resolved.sortBy)
  const [selectedThenByOption, setSelectedThenByOption] =
    useState<SortOrderItem>(resolved.thenBy)

  useEffect(() => {
    setSelectedSortByOption(resolved.sortBy)
    setSelectedThenByOption(resolved.thenBy)
  }, [resolved.sortBy, resolved.thenBy])

  const onChangeSortBy = (newValue: unknown): void => {
    const value = newValue as SortOrderItem | null
    if (!value) {
      return
    }
    setSelectedSortByOption(value)
    const nextSort: SortOrder = [value]
    if (selectedThenByOption) {
      nextSort.push(selectedThenByOption)
    }
    onChange(nextSort)
  }

  const onChangeThenBy = (newValue: unknown): void => {
    const value = newValue as SortOrderItem | null
    if (!value) {
      return
    }
    setSelectedThenByOption(value)
    const nextSort: SortOrder = [selectedSortByOption, value]
    onChange(nextSort)
  }

  const getSortByOptions = (): SortOrderItem[] =>
    options.filter(
      (option) =>
        !(
          option.dir === selectedThenByOption.dir &&
          option.property === selectedThenByOption.property
        )
    )

  const getThenByOptions = (): SortOrderItem[] =>
    options.filter(
      (option) =>
        !(
          option.dir === selectedSortByOption.dir &&
          option.property === selectedSortByOption.property
        )
    )

  return (
    <SortGrid>
      <FieldWrapper>
        <Label>
          <I18n name="sortBy" />
        </Label>
        <ReactSelectWrapper>
          <ReactSelect
            value={selectedSortByOption}
            onChange={onChangeSortBy}
            options={getSortByOptions()}
            getOptionLabel={(option: unknown) => (option as Option).label}
            getOptionValue={(option: unknown) =>
              `${(option as SortOrderItem).property}-${(option as SortOrderItem).dir}`
            }
          />
        </ReactSelectWrapper>
      </FieldWrapper>
      <FieldWrapper>
        <Label>
          <I18n name="thenBy" />
        </Label>
        <ReactSelectWrapper>
          <ReactSelect
            value={selectedThenByOption}
            onChange={onChangeThenBy}
            options={getThenByOptions()}
            getOptionLabel={(option: unknown) => (option as Option).label}
            getOptionValue={(option: unknown) =>
              `${(option as SortOrderItem).property}-${(option as SortOrderItem).dir}`
            }
          />
        </ReactSelectWrapper>
      </FieldWrapper>
    </SortGrid>
  )
}

const SortGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 20px;
  border-top: 1px solid ${defaultTheme.colors.border};
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const Label = styled.span`
  font-family: Roboto, serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${defaultTheme.colors.text};
`

const ReactSelectWrapper = styled.div`
  width: 100%;
  min-width: 0;
`
